<template>
  <div class="order-details-page">
    <vue-element-loading
      :active="appLoading"
      spinner="bar-fade-scale"
      color="#3ea56e"
      size="128"
      is-full-screen
    />

    <!-- Snackbar Notification -->
    <v-snackbar v-model="showSnackBar" color="#1e293b" right :timeout="timeout" class="modern-snackbar">
      <div class="d-flex align-center justify-space-between w-100 px-3 py-1">
        <span class="white--text font-weight-medium">{{ msg }}</span>
        <v-btn icon small @click="showSnackBar = false" class="ml-2">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Shipment Details Dialog -->
    <v-dialog v-model="shippedDialog" max-width="450px" persistent>
      <v-card class="dialog-card">
        <div class="dialog-header">
          <div class="dialog-icon-wrap">
            <v-icon color="white" size="26">mdi-truck-delivery-outline</v-icon>
          </div>
          <h3 class="dialog-title">Shipment Details</h3>
          <p class="dialog-subtitle">Enter tracking details to mark this order as Shipped.</p>
        </div>
        <v-card-text class="pt-4 px-6 pb-2">
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="shippedService"
                label="Shipped Service"
                placeholder="e.g. FedEx, BlueDart"
                outlined
                dense
                hide-details
                class="modern-input mb-4"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="trackingId"
                label="Tracking ID"
                placeholder="e.g. 123456789"
                outlined
                dense
                hide-details
                class="modern-input"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="dialog-actions px-6 pb-6">
          <v-btn
            outlined
            color="#9e9e9e"
            class="dialog-btn cancel-btn"
            @click="cancelShippedDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="#3ea56e"
            class="dialog-btn confirm-btn white--text"
            depressed
            @click="confirmShippedStatus"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-layout v-if="ServerError" wrap justify-center>
      <v-flex px-5 pt-5 xs12 sm12 md12 lg12 xl12 text-center>
        <ServerError />
      </v-flex>
    </v-layout>

    <v-layout v-else wrap justify-center>
      <v-flex xs12 sm12 md12 lg12 xl12 pa-5>
        
        <!-- Page Header -->
        <div class="page-header mb-5">
          <div class="header-left">
            <v-btn icon class="back-btn mr-3" @click="$router.go(-1)">
              <v-icon color="#2d8257">mdi-arrow-left</v-icon>
            </v-btn>
            <div class="header-icon-wrap">
              <v-icon color="white" size="22">mdi-receipt-text-outline</v-icon>
            </div>
            <div>
              <h2 class="page-title">Order Details</h2>
            </div>
          </div>
          <div class="header-actions" v-if="purchase">
            <div class="stat-chip">
              <v-icon size="14" color="#3ea56e" class="mr-1">mdi-tag-outline</v-icon>
              <span>ID: {{purchase.orderID || (purchase._id ? '#' + purchase._id : 'N/A') }}</span>
            </div>
          </div>
        </div>

        <div v-if="purchase">
          <!-- Overview Cards -->
          <v-row class="mb-5">
            <!-- Purchase/Order details -->
            <v-col cols="12" md="6" class="py-2">
              <v-card class="modern-card h-100" elevation="0">
                <div class="card-toolbar py-3 px-4">
                  <div class="toolbar-left">
                    <v-icon color="#3ea56e" class="mr-2">mdi-information-outline</v-icon>
                    <span class="toolbar-title">Purchase Details</span>
                  </div>
                </div>
                <v-divider></v-divider>
                <div class="pa-4">
                  <v-row dense>
                    <v-col cols="6" class="pb-3">
                      <router-link :to="'/customerDetails?user=' + purchase.uid._id" class="text-decoration-none">
                        <span class="info-label">Customer Name</span>
                        <div class="info-value mt-1 text-primary-link">
                          {{ purchase.uid.firstname }} {{ purchase.uid.lastname }}
                        </div>
                      </router-link>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Order Date</span>
                      <div class="info-value mt-1">{{ formatDate(purchase.create_date) }}</div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Phone</span>
                      <div class="info-value mt-1">{{ purchase.uid.phone || '-' }}</div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Email</span>
                      <div class="info-value mt-1">{{ purchase.uid.email || '-' }}</div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Total Amount</span>
                      <div class="info-value amount-text mt-1">Rs. {{ purchase.orderTotal || '0' }}</div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Payment Type</span>
                      <div class="info-value mt-1">{{ purchase.paymentType || '-' }}</div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Payment Status</span>
                      <div class="info-value mt-1">
                        <span :class="['status-pill', purchase.paymentStatus === 'Success' ? 'success-pill' : 'pending-pill']">
                          {{ purchase.paymentStatus || '-' }}
                        </span>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-card>
            </v-col>

            <!-- Delivery Address Details -->
            <v-col cols="12" md="6" class="py-2">
              <v-card class="modern-card h-100" elevation="0">
                <div class="card-toolbar py-3 px-4">
                  <div class="toolbar-left">
                    <v-icon color="#3ea56e" class="mr-2">mdi-map-marker-outline</v-icon>
                    <span class="toolbar-title">Delivery Address</span>
                  </div>
                </div>
                <v-divider></v-divider>
                <div class="pa-4" v-if="purchase.deliveryAddress">
                  <v-row dense>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Recipient Name</span>
                      <div class="info-value mt-1">
                        {{ purchase.deliveryAddress.firstname }} {{ purchase.deliveryAddress.lastname }}
                      </div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Recipient Phone</span>
                      <div class="info-value mt-1">{{ purchase.deliveryAddress.delphone || '-' }}</div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Street Address</span>
                      <div class="info-value mt-1">{{ purchase.deliveryAddress.streetAddress || '-' }}</div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Recipient Email</span>
                      <div class="info-value mt-1">{{ purchase.deliveryAddress.email || '-' }}</div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Locality / City</span>
                      <div class="info-value mt-1">
                        {{ purchase.deliveryAddress.locality || '-' }}, {{ purchase.deliveryAddress.city || '-' }}
                      </div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">State / Pincode</span>
                      <div class="info-value mt-1">
                        {{ purchase.deliveryAddress.state || '-' }} - {{ purchase.deliveryAddress.pincode || '-' }}
                      </div>
                    </v-col>
                    <v-col cols="6" class="pb-3">
                      <span class="info-label">Country</span>
                      <div class="info-value mt-1">{{ purchase.deliveryAddress.country || '-' }}</div>
                    </v-col>
                  </v-row>
                </div>
                <div class="pa-4 text-center text--secondary" v-else>
                  No delivery address details found.
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Product Details Section Title -->
          <div class="section-title mb-4">
            <v-icon color="#1e293b" class="mr-2">mdi-basket-outline</v-icon>
            <span class="title-text">Ordered Items</span>
          </div>

          <!-- Items grouped by Seller -->
          <div v-if="seller && seller.length > 0">
            <v-card
              v-for="item in seller"
              :key="item._id._id"
              class="modern-card mb-5"
              elevation="0"
            >
              <!-- Seller Header -->
              <div class="seller-header px-4 py-3 d-flex align-center justify-space-between flex-wrap gap-2">
                <div class="d-flex align-center">
                  <v-icon color="#3ea56e" class="mr-2">mdi-store-outline</v-icon>
                  <span class="seller-org-name">{{ item._id ? item._id.organization : 'N/A' }}</span>
                </div>
                
                <div v-if="item._id && item._id.isInvoiceAvailable" class="invoice-btn-wrap">
                  <v-btn
                    depressed
                    height="32px"
                    class="action-download-btn text-none px-3"
                    target="_blank"
                    :href="
                      baseURL +
                        '/order/invoice/pdf?orderId=' +
                        $route.query.id +
                        '&sellerId=' +
                        item._id._id
                    "
                  >
                    <v-icon left size="16" color="#ef4444">mdi-file-pdf-box</v-icon>
                    <span style="font-size: 12px;">Invoice</span>
                  </v-btn>
                </div>
              </div>
              <v-divider></v-divider>

              <!-- Items List -->
              <div class="pa-4" v-if="item.items">
                <div
                  v-for="(list, j) in item.items"
                  :key="j"
                  class="product-row-wrap"
                  :class="{ 'border-bottom': j < item.items.length - 1 }"
                >
                  <v-row align="start" class="py-4">
                    <!-- Photo column -->
                    <v-col cols="12" sm="3" md="2" class="d-flex justify-center justify-sm-start">
                      <div class="product-photo-wrap">
                        <div @click.prevent="handleProductClick(list.productId)" style="cursor: pointer;">
                          <v-img
                            :src="baseURL + '/wp/' + (list.productId && list.productId.photos && list.productId.photos.length > 0 ? list.productId.photos[0] : '')"
                            aspect-ratio="1"
                            contain
                            height="80px"
                            width="80px"
                            class="rounded-lg"
                          ></v-img>
                        </div>
                      </div>
                    </v-col>

                    <!-- Details column -->
                    <v-col cols="12" sm="9" md="6">
                      <div class="product-details-content">
                        <div
                          @click.prevent="handleProductClick(list.productId)"
                          class="product-link text-decoration-none"
                          style="cursor: pointer;"
                        >
                          <h4 class="product-name-title">{{ list.productname }}</h4>
                        </div>
                        
                        <!-- Details Grid -->
                        <v-row class="mt-3" dense>
                          <v-col cols="4" sm="3">
                            <span class="info-label">Price</span>
                            <div class="info-value mt-1">Rs. {{ filterValue(list.price) }}</div>
                          </v-col>
                          <v-col cols="4" sm="3">
                            <span class="info-label">Offer</span>
                            <div class="info-value offer-text mt-1">{{ list.offer }}% Off</div>
                          </v-col>
                          <v-col cols="4" sm="3">
                            <span class="info-label">Offer Price</span>
                            <div class="info-value mt-1">Rs. {{ filterValue(list.offerPrice) }}</div>
                          </v-col>
                          <v-col cols="4" sm="3">
                            <span class="info-label">Quantity</span>
                            <div class="info-value mt-1">{{ list.quantity }}</div>
                          </v-col>
                        </v-row>

                        <!-- GST & Taxes Breakdown Box -->
                        <div
                          class="taxes-breakdown-box mt-3 pa-3"
                          v-if="list.taxableAmount !== undefined && list.taxableAmount !== null"
                        >
                          <v-row dense>
                            <v-col cols="6" sm="4">
                              <span class="tax-label">Taxable Amount</span>
                              <div class="tax-value">Rs. {{ Number(list.taxableAmount.toFixed(2)) }}</div>
                            </v-col>
                            <v-col cols="6" sm="4">
                              <span class="tax-label">GST Total</span>
                              <div class="tax-value">Rs. {{ Number(list.taxAmount.toFixed(2)) }}</div>
                            </v-col>
                            <v-col cols="6" sm="4">
                              <span class="tax-label">CGST / SGST</span>
                              <div class="tax-value">
                                Rs. {{ Number((list.taxAmount / 2).toFixed(2)) }} / Rs. {{ Number((list.taxAmount / 2).toFixed(2)) }}
                              </div>
                            </v-col>
                            <v-col cols="6" sm="4" v-if="list.totalPackingCharge">
                              <span class="tax-label">Packing Charge</span>
                              <div class="tax-value">Rs. {{ list.totalPackingCharge }}</div>
                            </v-col>
                            <v-col cols="6" sm="4" v-if="list.totalDeliveryCharge">
                              <span class="tax-label">Delivery Charge</span>
                              <div class="tax-value">Rs. {{ list.totalDeliveryCharge }}</div>
                            </v-col>
                            <v-col cols="6" sm="4">
                              <span class="tax-label">Final Subtotal</span>
                              <div class="tax-value font-weight-bold emerald-text">
                                Rs. {{ filterValue(list.TotalAmount) }}
                              </div>
                            </v-col>
                          </v-row>
                        </div>
                      </div>
                    </v-col>

                    <!-- Order Status and Seller details column -->
                    <v-col cols="12" sm="12" md="4" class="pl-md-4 mt-4 mt-md-0">
                      <v-row dense>
                        <!-- Order Status Control -->
                        <v-col cols="12" sm="6" md="12" class="pb-3">
                          <span class="info-label mb-1 d-block">Order Status</span>
                          <div class="d-flex align-center mb-2">
                            <span :class="['status-badge', getStatusClass(list.orderStatus)]">
                              {{ list.orderStatus }}
                            </span>
                          </div>
                          <v-select
                            v-model="list.orderStatus"
                            :items="status"
                            item-text="text"
                            item-value="value"
                            outlined
                            dense
                            hide-details
                            class="modern-input"
                            @change="changeStatus(list)"
                          ></v-select>
                        </v-col>

                        <!-- Seller Details Box -->
                        <v-col cols="12" sm="6" md="12">
                          <div class="seller-details-subcard pa-3">
                            <span class="info-label d-block mb-1">Seller Contact Details</span>
                            <div class="seller-sub-org">{{ list.sellerId.organization }}</div>
                            <div class="seller-sub-info mt-1">
                              <v-icon size="12" class="mr-1">mdi-email-outline</v-icon>
                              <span>{{ list.sellerId.email }}</span>
                            </div>
                            <div class="seller-sub-info mt-1">
                              <v-icon size="12" class="mr-1">mdi-map-marker-outline</v-icon>
                              <span>{{ list.sellerId.locality }}, {{ list.sellerId.city }} - {{ list.sellerId.pincode }}</span>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card>
          </div>
          <div class="pa-5 text-center text--secondary" v-else>
            No products/items found for this order.
          </div>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: ["purchaseid"],
  data() {
    return {
      purchase: null,
      showSnackBar: false,
      msg: null,
      timeout: 5000,
      deliverydate: null,
      deliverytime: null,
      products: [],
      receipts: [],
      product: [],
      deliverystatus: [],
      productdetails: [],
      details: [],
      seller: [],
      search: "",
      count: 10,
      date: new Date().toISOString().substr(0, 10),
      menu: false,
      time: null,
      time3: null,
      menu2: false,
      appLoading: false,
      ServerError: false,
      userStatus: null,
      userRole: localStorage.getItem("role"),
      headers: [
        { text: " Product Name", value: "Productname", sortable: false },
        { text: "Seller", value: "Seller", sortable: false },
        { text: "Image", value: "dealtype", sortable: false },
        { text: "Price", value: "Price", sortable: false },
        { text: "Quantity", value: "Quantity", sortable: false },
        { text: "Total Price", value: "tPrice", sortable: false },
        {
          text: "Order Status",
          value: "status",
          sortable: false,
          width: "20%",
        },
      ],
      receipt: [
        { value: "Products", text: "Products", tdClass: "tdwidth" },
        { value: "Seller", text: "Seller " },
        { value: "Receipt", text: "Receipt" },
      ],
      status: [
        { value: "Pending", text: "Pending" },
        { value: "Order Confirmed", text: "Order Confirmed" },
        { value: "Cancelled By Seller", text: "Cancelled By Seller" },
        { value: "Shipped", text: "Shipped" },
        { value: "Out For Delivery", text: "Out For Delivery" },
        { value: "Delivered", text: "Delivered" },
      ],
      Status: null,
      shippedDialog: false,
      shippedService: null,
      trackingId: null,
      tempItem: null,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    handleProductClick(product) {
      if (!product || product.status === 'Deleted' || product.status === 'Inactive') {
        this.msg = "Product not found";
        this.showSnackBar = true;
      } else {
        this.$router.push('/productDetails/' + product._id);
      }
    },
    getStatusClass(status) {
      if (!status) return 'status-pending';
      const s = status.toLowerCase();
      if (s.includes('pending')) return 'status-pending';
      if (s.includes('confirm')) return 'status-confirmed';
      if (s.includes('cancel')) return 'status-cancelled';
      if (s.includes('ship')) return 'status-shipped';
      if (s.includes('delivery') || s.includes('out for')) return 'status-out-for-delivery';
      if (s.includes('deliver')) return 'status-delivered';
      return 'status-pending';
    },
    filterValue(value) {
      return Number(value).toLocaleString();
    },
    addTime() {
      this.time = this.time3;
      this.menu2 = false;
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/admin/order/view/v1",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.purchase = response.data.data;
          this.seller = response.data.sellerItems;
        })
        .catch((e) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(e);
        });
    },
    changeStatus(d) {
      if (d.orderStatus === "Shipped") {
        this.tempItem = d;
        this.shippedService = null;
        this.trackingId = null;
        this.shippedDialog = true;
      } else {
        this.submitStatusChange(d);
      }
    },
    cancelShippedDialog() {
      this.shippedDialog = false;
      this.getData();
    },
    confirmShippedStatus() {
      this.shippedDialog = false;
      this.submitStatusChange(this.tempItem, {
        shippedService: this.shippedService,
        trackingId: this.trackingId,
      });
    },
    submitStatusChange(d, extraData = {}) {
      this.appLoading = true;
      var data = {};
      data["orderStatus"] = d.orderStatus;
      data["orderId"] = d.orderId;
      data["itemId"] = d._id;
      if (extraData.shippedService) {
        data["shippedService"] = extraData.shippedService;
      }
      if (extraData.trackingId) {
        data["trackingId"] = extraData.trackingId;
      }
      axios({
        method: "POST",
        url: "/admin/seller/ordered/item/status/change",
        data: data,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.getData();
            this.showSnackBar = true;
            this.msg = response.data.msg;
          } else {
            this.getData();
            this.showSnackBar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((e) => {
          this.appLoading = false;
          this.showSnackBar = true;
          console.log(e);
        });
    },
    formatDate(item) {
      if (!item) return "";
      var dt = new Date(item);
      if (isNaN(dt.getTime())) return "";
      var day = dt.getDate();
      var year = dt.getFullYear();
      var hours = dt.getHours();
      var minutes = dt.getMinutes();
      dt = dt.toString();
      var ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      minutes = minutes < 10 ? "0" + minutes : minutes;
      var strTime =
        day +
        " " +
        dt.slice(4, 7) +
        " " +
        year +
        " " +
        hours +
        ":" +
        minutes +
        " " +
        ampm;
      return strTime;
    },
  },
};
</script>

<style scoped>
/* Page Wrapper */
.order-details-page {
  background: #f5f6fa;
  min-height: 100vh;
  font-family: poppinsmedium, sans-serif;
  width: 100%;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.back-btn {
  background-color: #ffffff !important;
  border: 1px solid #c8e6c9 !important;
  box-shadow: 0 2px 5px rgba(0,0,0,0.04) !important;
}
.header-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3ea56e, #2d8257);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(62, 165, 110, 0.35);
}
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}
.stat-chip {
  display: flex;
  align-items: center;
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #2d8257;
  height: 38px;
}

/* Modern Card */
.modern-card {
  border-radius: 16px !important;
  border: 1px solid #ebebeb !important;
  overflow: hidden;
  background: #ffffff;
}

/* Card Toolbar */
.card-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fafbfc;
}
.toolbar-left {
  display: flex;
  align-items: center;
}
.toolbar-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

/* Info Elements */
.info-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.5px;
  display: block;
}
.info-value {
  font-size: 13px;
  color: #334155;
  font-weight: 600;
}
.text-primary-link {
  color: #2d8257;
  text-decoration: underline;
}
.amount-text {
  font-size: 16px;
  color: #10b981;
  font-weight: 700;
}

/* Status Pills */
.status-pill {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}
.success-pill {
  background-color: #e8f7ee;
  color: #3ea56e;
}
.pending-pill {
  background-color: #fff3e0;
  color: #f76707;
}

/* Section Title */
.section-title {
  display: flex;
  align-items: center;
  margin-top: 24px;
}
.title-text {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

/* Seller Header */
.seller-header {
  background: #f8fafc;
}
.seller-org-name {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

/* Product row */
.product-row-wrap {
  padding: 8px 0;
}
.product-row-wrap.border-bottom {
  border-bottom: 1px dashed #e2e8f0;
}
.product-photo-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 4px;
  background: #fafbfc;
  display: inline-block;
  overflow: hidden;
}
.product-link {
  color: inherit;
}
.product-name-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  transition: color 0.2s ease;
}
.product-name-title:hover {
  color: #3ea56e;
}
.offer-text {
  color: #3ea56e;
}

/* Taxes Box */
.taxes-breakdown-box {
  background: #f8fafc;
  border: 1px solid #eaedf0;
  border-radius: 8px;
}
.tax-label {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
  display: block;
}
.tax-value {
  font-size: 12px;
  color: #475569;
  font-weight: 600;
}
.emerald-text {
  color: #10b981;
}

/* Seller sub-card */
.seller-details-subcard {
  background: #fafbfc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.seller-sub-org {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}
.seller-sub-info {
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
}

/* Modern inputs */
.modern-input ::v-deep .v-input__slot {
  border-radius: 8px !important;
  font-size: 13px;
  border-color: #e2e8f0 !important;
}

/* Action button */
.action-download-btn {
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  color: #334155 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0px !important;
  transition: all 0.2s ease !important;
}
.action-download-btn:hover {
  background-color: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px);
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  border-width: 1px;
  border-style: solid;
}
.status-pending {
  background-color: #fff3e0;
  color: #f76707;
  border-color: #ffa94d33;
}
.status-confirmed {
  background-color: #e7f5ff;
  color: #228be6;
  border-color: #748ffc33;
}
.status-cancelled {
  background-color: #ffe3e3;
  color: #fa5252;
  border-color: #ffa8a833;
}
.status-shipped {
  background-color: #f8f0fc;
  color: #be4bdb;
  border-color: #da77f233;
}
.status-out-for-delivery {
  background-color: #e6fcf5;
  color: #0ca678;
  border-color: #38d9a933;
}
.status-delivered {
  background-color: #e8f7ee;
  color: #3ea56e;
  border-color: #3ea56e33;
}

/* Dialog styling */
.dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.dialog-header {
  padding: 24px 24px 12px;
  text-align: center;
  background: #f0fdf4;
}
.dialog-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3ea56e, #2d8257);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  box-shadow: 0 4px 12px rgba(62, 165, 110, 0.3);
}
.dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}
.dialog-subtitle {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 24px 20px !important;
}
.dialog-btn {
  border-radius: 8px !important;
  padding: 8px 20px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}
.cancel-btn {
  border-color: #e2e8f0 !important;
  color: #64748b !important;
}
.confirm-btn {
  box-shadow: 0 4px 10px rgba(62, 165, 110, 0.2) !important;
}

.tdwidth {
  width: 50% !important;
}
</style>
